import { ChevronDown } from 'lucide-react';
import styles from './Select.module.css';

export interface SelectOption {
    value: string | number;
    label: string;
    disabled?: boolean;
    icon?: React.ReactNode;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
    options: SelectOption[];
    value?: string | number;
    onChange?: (value: string | number) => void;
    label?: string;
    error?: string;
    placeholder?: string;
    fullWidth?: boolean;
    noMargin?: boolean; // New prop to remove default margin
}

export const Select = ({
    options,
    value,
    onChange,
    label,
    error,
    placeholder,
    fullWidth = false,
    noMargin = false,
    className = '',
    disabled = false,
    ...props
}: SelectProps) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (onChange) {
            const newValue = e.target.value;
            // Try to convert to number if the original value was a number
            const option = options.find(opt => String(opt.value) === newValue);
            onChange(option?.value ?? newValue);
        }
    };

    return (
        <div className={`${styles.selectField} ${error ? styles.selectFieldError : ''} ${fullWidth ? styles.fullWidth : ''} ${noMargin ? styles.noMargin : ''}`}>
            {label && <label htmlFor={props.id}>{label}</label>}
            <div className={styles.selectWrapper}>
                <select
                    className={`${styles.select} ${className}`}
                    value={value}
                    onChange={handleChange}
                    disabled={disabled}
                    {...props}
                >
                    {placeholder && (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    )}
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>
                <ChevronDown className={styles.chevron} size={16} />
            </div>
            {error && <span className={styles.errorMessage}>{error}</span>}
        </div>
    );
};
